import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

const ChatLayoutContext = createContext(null)

/**
 * Tracks UI-state that's shared by every layout (open/closed,
 * suggestions, input focus). Layouts read from this context instead of
 * hoisting open/close into the topmost App component, which lets
 * `window.OmetricsAI_API` toggle the panel imperatively without going
 * through the React tree.
 */
export function ChatLayoutProvider({ children, initialOpen = false }) {
  const [open, setOpen] = useState(initialOpen)
  const [suggestions, setSuggestions] = useState([])
  const [composerFocused, setComposerFocused] = useState(false)

  const openChat = useCallback(() => setOpen(true), [])
  const closeChat = useCallback(() => setOpen(false), [])
  const toggleChat = useCallback(() => setOpen((prev) => !prev), [])

  const value = useMemo(
    () => ({
      open,
      openChat,
      closeChat,
      toggleChat,
      suggestions,
      setSuggestions,
      composerFocused,
      setComposerFocused,
    }),
    [open, openChat, closeChat, toggleChat, suggestions, composerFocused],
  )

  return (
    <ChatLayoutContext.Provider value={value}>
      {children}
    </ChatLayoutContext.Provider>
  )
}

export function useChatLayout() {
  const ctx = useContext(ChatLayoutContext)
  if (!ctx)
    throw new Error('useChatLayout must be used inside <ChatLayoutProvider>')
  return ctx
}
